import React, { memo, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import GenericFilter from '../../Generics/GenericFilter';

function UnwindingProposalFilter({ onFilter, onClear, options, filterNames }) {

  const [filters, setFilters] = useState([
    {
      name: 'status',
      title: 'Status da Proposta',
      placeholder: ' ',
      type: 'default',
      options: [],
    },
    {
      name: 'proposalDates',
      title: 'Data de Envio',
      placeholder: ' ',
      type: 'default',
      options: [],
    },
    {
      name: 'advisorCodes',
      title: 'Assessores',
      placeholder: ' ',
      type: 'default',
      options: [],
    },
    {
      name: 'clients',
      title: 'Clientes',
      placeholder: ' ',
      type: 'default',
      options: [],
    },
    {
      name: 'tickers',
      title: 'Ativos',
      placeholder: ' ',
      type: 'default',
      options: [],
    },
    {
      name: 'structures',
      title: 'Estruturas',
      placeholder: ' ',
      type: 'default',
      options: [],
    },
    {
      name: 'operationType',
      title: 'Tipo de Operações',
      placeholder: ' ',
      type: 'default',
      options: [],
    },
  ]);

  const getOptionSelected = useCallback(option => option?.map(({ id, isSelected }) => (isSelected ? id : undefined))?.filter(data => !!data) || [], []);

  const createOptions = useCallback(optionData => optionData?.map(option => ({
    id: `${option}`,
    value: option,
    label: `${option}`,
    isSelected: false,
  })), []);

  const createOptionsWithObjectId = useCallback(optionData => optionData?.map(option => ({
    id: `${option.key}`,
    value: option.key,
    label: `${option.value}`,
    isSelected: false,
  })), []);

  const createFilter = useCallback(() => {
    const STATUS = filters[0];
    const PROPOSALDATES = filters[1];
    const ADVISORS = filters[2];
    const CLIENTS = filters[3];
    const TICKERS = filters[4];
    const STRUCTURES = filters[5];
    const OPERATIONTYPE = filters[6];

    const HAS_STATUS_OPTIONS = STATUS.options.length === 0;
    const HAS_PROPOSALDATES_OPTIONS = PROPOSALDATES.options.length === 0;
    const HAS_ADVISORS_OPTIONS = ADVISORS.options.length === 0;
    const HAS_CLIENTS_OPTIONS = CLIENTS.options.length === 0;
    const HAS_TICKERS_OPTIONS = TICKERS.options.length === 0;
    const HAS_STRUCTURES_OPTIONS = STRUCTURES.options.length === 0;
    const HAS_OPERATIONSTYPE_OPTIONS = OPERATIONTYPE.options.length === 0;

    if (
      HAS_STATUS_OPTIONS
      || HAS_PROPOSALDATES_OPTIONS
      || HAS_ADVISORS_OPTIONS
      || HAS_CLIENTS_OPTIONS
      || HAS_TICKERS_OPTIONS
      || HAS_STRUCTURES_OPTIONS
      || HAS_OPERATIONSTYPE_OPTIONS
    ) {

      setFilters([
        { ...STATUS, options: createOptionsWithObjectId(options?.Status) },
        { ...PROPOSALDATES, options: createOptions(options?.ProposalDates) },
        { ...ADVISORS, options: createOptions(options?.AdvisorCodes) },
        { ...CLIENTS, options: createOptions(options?.Clients) },
        { ...TICKERS, options: createOptions(options?.Tickers) },
        { ...STRUCTURES, options: createOptions(options?.Structures) },
        { ...OPERATIONTYPE, options: createOptions(options?.OperationType) },
      ]);
    }
  }, [filters, options]);

  const handleOnFilter = useCallback((filterData) => {
    onFilter({
      Status: getOptionSelected(filterData[0].options),
      ProposalDates: getOptionSelected(filterData[1].options),
      AdvisorCodes: getOptionSelected(filterData[2].options),
      Clients: getOptionSelected(filterData[3].options),
      Tickers: getOptionSelected(filterData[4].options),
      Structures: getOptionSelected(filterData[5].options),
      OperationType: getOptionSelected(filterData[6].options),
    });
  }, []);

  const handleOnClear = useCallback(() => {
  const STATUS = filters[0];
  const PROPOSALDATES = filters[1];
  const ADVISORS = filters[2];
  const CLIENTS = filters[3];
  const TICKERS = filters[4];
  const STRUCTURES = filters[5];
  const OPERATIONTYPE = filters[6];

  setFilters([
    { ...STATUS, options: createOptionsWithObjectId(options?.Status) },
    { ...PROPOSALDATES, options: createOptions(options?.ProposalDates) },
    { ...ADVISORS, options: createOptions(options?.AdvisorCodes) },
    { ...CLIENTS, options: createOptions(options?.Clients) },
    { ...TICKERS, options: createOptions(options?.Tickers) },
    { ...STRUCTURES, options: createOptions(options?.Structures) },
    { ...OPERATIONTYPE, options: createOptions(options?.OperationType) },
  ]);

  onClear({
      Status: [],
      ProposalDates: [],
      AdvisorCodes: [],
      Clients: [],
      Tickers: [],
      Structures: [],
      OperationType: [],
    });
  }, [options]);

  useEffect(() => {
    createFilter();
  }, [options]);

  return (
    <GenericFilter
      onFilter={handleOnFilter}
      onClear={handleOnClear}
      filters={filters}
      filterNames={filterNames}
    >
    </GenericFilter>
  );
}

UnwindingProposalFilter.propTypes = {
  onFilter: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  options: PropTypes.shape({
    Status: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object.isRequired]).isRequired).isRequired,
    ProposalDates: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired]).isRequired).isRequired,
    AdvisorCodes: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string]).isRequired).isRequired,
    Clients: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string]).isRequired).isRequired,
    Tickers: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired]).isRequired).isRequired,
    Structures: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired]).isRequired).isRequired,
    OperationType: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired]).isRequired).isRequired,
  }).isRequired,
};

export default memo(UnwindingProposalFilter);
